import type { EpicDetail } from '../../../../../shared/types/epics';

// ---------------------------------------------------------------------------
// Shared formatters + small pure helpers used across the epic detail page's
// header / tabs / stories / plan / cost / diff modules. Split out of the old
// monolithic page.tsx (UX-05) so every module can import just what it needs
// instead of reaching back into the page file.
// ---------------------------------------------------------------------------

export function formatDate(iso: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export function formatCost(usd: number): string {
  if (!usd || usd === 0) return '$0';
  if (usd < 0.01) return `$${usd.toFixed(6)}`;
  return `$${usd.toFixed(4)}`;
}

export const usdSp = (n: number): string => `$${n.toFixed(3)}`;

export function cpsColor(cps: number): string {
  if (cps <= 0.2) return '#22A06B';
  if (cps <= 0.4) return '#E2A100';
  return '#E5484D';
}

export function pct(ratio: number): string {
  return `${Math.round(ratio * 100)}%`;
}

/** DOM-safe anchor for a story id (cross-repo ids carry "/" and "#"). */
export function storyAnchorId(id: string): string {
  return `story-${id.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
}

/**
 * FORGE-225: total spend on an epic, from durable per-story lifetime cost
 * (survives the 90-day SESSION TTL) plus ungrouped "setup" runs (planner,
 * graph) not attached to a story row. `epic.totalCostUsd` / `epicTotalCostUsd`
 * come from META and are unreliable mid-flight — 0 until completion, and never
 * written for some cross-repo epics — so we sum the live rows instead.
 */
export function epicSpend(epic: EpicDetail): { stories: number; setup: number; total: number } {
  const stories = epic.stories.reduce((sum, s) => sum + (s.lifetimeCostUsd || 0), 0);
  const storyKeys = new Set<string>();
  for (const s of epic.stories) {
    storyKeys.add(s.id);
    if (s.numericId !== null) storyKeys.add(String(s.numericId));
  }
  const setup = epic.sessions
    .filter((s) => !storyKeys.has(s.storyId))
    .reduce((sum, s) => sum + (s.costUsd || 0), 0);
  return {
    stories: Number(stories.toFixed(6)),
    setup: Number(setup.toFixed(6)),
    total: Number((stories + setup).toFixed(6)),
  };
}
